import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef
} from 'react';
import {
  Close,
  ExpandMore,
  NavigateNext,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import {
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grow,
  Hidden,
  IconButton,
  InputAdornment,
  Paper,
  Popper
} from '@mui/material';
import ButtonComponent from 'components/design-system/Button/Button';
import Heading from 'components/design-system/Heading/Heading';
import InputComponent from 'components/design-system/input/Input';
import LabelComponent from 'components/design-system/Label/Label';
import LinkComponent from 'components/design-system/Link/Link';
import Text from 'components/design-system/Text/Text';
import authenticationService from 'services/authentication';
import { AuthContext } from 'context';
import { Form } from 'formik';
import { AlertaError, AlertaExito } from 'utils/Alertas';

export const LoginForm = React.forwardRef(
  ({ handleChange, values, setFieldValue, errors }, ref) => {
    const [sendingEmail, setSendingEmail] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [open, setOpen] = useState(false);
    const { logeduser } = useContext(AuthContext);

    // PARA EL FUNCIONAMIENTO DEL MENSAJE DE CONFIRMACIÓN
    const [confirmChangePassword, setConfirmChangePassword] = useState(null);

    const handleClickOpen = (row, table) => {
      setOpen(true);
    };

    const handleClose = () => {
      setOpen(false);
      // handleReset(); // TODO: opcional se debe preguntar
    };

    const handleClickShowPassword = () => setShowPassword(show => !show);

    const handleMouseDownPassword = event => {
      event.preventDefault();
    };

    const handleClickRecuperarPass = useCallback(async () => {
      if (sendingEmail) return;

      setSendingEmail(true);

      const email = values.email.trim();
      await authenticationService
        .recuperarPass(email, false)
        .then(result => {
          const { status } = result;
          if (status === 200) {
            AlertaExito('Correo enviado');
          }
          setSendingEmail(false);
        })
        .catch(error => {
          if (
            error.response &&
            error.response.data.error.codigo === 'UV404-1'
          ) {
            AlertaError('Correo no registrado');
          } else {
            AlertaError('Error al enviar correo');
          }
          setSendingEmail(false);
          setConfirmChangePassword(false);
        });
      setConfirmChangePassword(null);
    }, [values.email, sendingEmail]);

    useEffect(() => {
      if (!values.email || !values.email.trim()) {
        return;
      }
      if (confirmChangePassword) {
        handleClickRecuperarPass();
      }
    }, [confirmChangePassword, handleClickRecuperarPass, values.email]);

    useEffect(() => {
      if (logeduser?.expiredUserEmail) {
        setFieldValue('email', logeduser?.expiredUserEmail);
      }
    }, [logeduser?.expiredUserEmail, setFieldValue]);

    return (
      <>
        <Form id="login-form" className="login-body">
          <div className="mb-4">
            <InputComponent
              fullWidth
              onChange={event => {
                // Convierte email a minusculas
                setFieldValue('email', event.target.value.toLowerCase().trim());
              }}
              label={<LabelComponent>Email</LabelComponent>}
              name="email"
              type="text"
              value={values.email.toLowerCase().trim() || ''}
              variant="outlined"
              touched={true}
              estado={
                errors.email ? { tipo: 'error', mensaje: errors.email } : null
              }
              // estado={inputFeedbackEmail}
            />
          </div>
          <div className="mb-4">
            <InputComponent
              fullWidth
              label={<LabelComponent>Contraseña</LabelComponent>}
              onChange={handleChange}
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={values.password || ''}
              variant="outlined"
              touched={true}
              // estado={inputFeedbackPassword}
              estado={
                errors.password
                  ? { tipo: 'error', mensaje: errors.password }
                  : null
              }
              outerInputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </div>
          <div className="mb-3">
            <ButtonComponent
              type="primary"
              form="login-form"
              buttonType="submit"
              fullWidth
              disabled={sendingEmail}
              // onClick={handleIngresar}
              className="flex justify-between"
            >
              Iniciar Sesión
              <NavigateNext />
            </ButtonComponent>
            <input
              type="submit"
              value="Send Request"
              style={{ display: 'none' }}
            />
          </div>

          <Text>
            <LinkComponent
              type="a"
              onClick={() => {
                handleClickOpen();
              }}
            >
              {sendingEmail
                ? 'Enviando Correo...'
                : '¿Olvidaste tu contraseña?'}
            </LinkComponent>
          </Text>
        </Form>

        <Dialog
          open={open}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle
            id="alert-dialog-title"
            className="border-b border-neutral-90"
          >
            <Heading type="h3" className="mb-0">
              {`Confirmación de cambio de contraseña `}
            </Heading>
          </DialogTitle>
          <DialogContent className="p-5">
            <DialogContentText id="alert-dialog-description" className="pt-4">
              <Text>
                {`¿Estás seguro que deseas reestablecer tu contraseña?`}
              </Text>
            </DialogContentText>
          </DialogContent>
          <DialogActions className="border-t border-neutral-90 p-4 justify-between">
            <ButtonComponent
              type="secondary"
              onClick={handleClose}
              className="justify-center"
            >
              <Hidden smDown>Cancelar</Hidden>
              <Hidden smUp>
                <Close />
              </Hidden>
            </ButtonComponent>
            <ButtonComponent
              type="primary"
              onClick={() => {
                setConfirmChangePassword(true);
                handleClose();
              }}
              autoFocus
              className="w-full xs:w-auto justify-center"
            >
              Confirmar
            </ButtonComponent>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

export const LoginFormContainer = ({ children }) => {
  const { logeduser } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const handleClose = useCallback(event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  }, []);

  const handleToggle = useCallback(() => {
    setOpen(prevOpen => !prevOpen);
  }, []);

  const prevOpen = useRef(open);

  useEffect(() => {
    if (prevOpen.current && !open) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  useEffect(() => {
    if (logeduser?.expiredUserEmail) {
      setTimeout(() => {
        setOpen(true);
      }, 1500);
    }
  }, [logeduser?.expiredUserEmail]);

  return (
    <>
      <div ref={anchorRef}>
        <ButtonComponent
          aria-controls={open ? 'menu-list-grow' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
          className="!px-2 sm:!px-4 !text-xs sm:text-sm mr-1"
        >
          <Hidden smDown>
            Iniciar Sesión
            <ExpandMore />
          </Hidden>
          <Hidden smUp>Ingresar</Hidden>
        </ButtonComponent>
      </div>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom'
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                {/* se comenta menuListItem ya que provoca problema al presionar la letra e  */}
                {/* <MenuList autoFocusItem={open} id="menu-list-grow"> */}
                <div className="loginPopUp">{children}</div>
                {/* </MenuList> */}
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};
